import * as XLSX from "xlsx";
import mammoth from "mammoth";

/** @returns {'image'|'pdf'|'spreadsheet'|'word'|'text'|'embed'} */
export function getDocumentPreviewMode(documentType, documentName) {
  const mime = String(documentType || "").toLowerCase();
  const ext =
    String(documentName || "")
      .split(".")
      .pop()
      ?.toLowerCase() || "";

  if (
    mime.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)
  ) {
    return "image";
  }

  if (mime === "application/pdf" || ext === "pdf") {
    return "pdf";
  }

  if (
    mime.includes("spreadsheet") ||
    mime.includes("excel") ||
    mime === "text/csv" ||
    ["xlsx", "xls", "csv"].includes(ext)
  ) {
    return "spreadsheet";
  }

  if (
    mime.includes("wordprocessing") ||
    mime.includes("msword") ||
    ["docx", "doc"].includes(ext)
  ) {
    return "word";
  }

  if (mime.startsWith("text/") || ext === "txt") {
    return "text";
  }

  return "embed";
}

function decodeBufferAsText(buffer) {
  const bytes =
    buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer);
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

function looksLikeHtmlSpreadsheet(text) {
  const sample = String(text || "").slice(0, 2048).toLowerCase();
  return (
    sample.includes("<html") ||
    sample.includes("<!doctype") ||
    sample.includes("<?xml") ||
    sample.includes("<workbook") ||
    (sample.includes("<table") && sample.includes("</table>"))
  );
}

function extractHtmlSpreadsheetContent(text) {
  const bodyMatch = String(text).match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const inner = bodyMatch ? bodyMatch[1] : text;
  if (/<table[\s>]/i.test(inner)) {
    return `<div class="doc-preview-html">${inner}</div>`;
  }
  return null;
}

function readSpreadsheetWorkbook(buffer) {
  const bytes =
    buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer);
  const text = decodeBufferAsText(buffer);

  if (looksLikeHtmlSpreadsheet(text)) {
    const htmlContent = extractHtmlSpreadsheetContent(text);
    if (htmlContent) {
      return { kind: "html", html: htmlContent };
    }
    try {
      const wb = XLSX.read(text, { type: "string" });
      if (Array.isArray(wb?.SheetNames) && wb.SheetNames.length) {
        return { kind: "workbook", wb };
      }
    } catch {
      /* try binary readers below */
    }
  }

  const attempts = [
    () => XLSX.read(bytes, { type: "array" }),
    () => XLSX.read(bytes, { type: "binary" }),
    () => XLSX.read(text, { type: "string" }),
  ];

  let lastError;
  for (const attempt of attempts) {
    try {
      const wb = attempt();
      if (Array.isArray(wb?.SheetNames) && wb.SheetNames.length) {
        return { kind: "workbook", wb };
      }
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Could not read this spreadsheet file.");
}

function buildSpreadsheetPreviewHtml(wb) {
  const sheetNames = Array.isArray(wb?.SheetNames) ? wb.SheetNames : [];
  if (!sheetNames.length) {
    throw new Error("This spreadsheet has no readable sheets.");
  }

  const html = sheetNames
    .map((name) => {
      const sheet = wb.Sheets?.[name];
      if (!sheet) return "";
      try {
        const table = XLSX.utils.sheet_to_html(sheet);
        if (!table) return "";
        return `<section class="doc-preview-sheet-wrap"><h4 class="doc-preview-sheet-title">${escapeHtml(name)}</h4>${table}</section>`;
      } catch {
        return `<section class="doc-preview-sheet-wrap"><h4 class="doc-preview-sheet-title">${escapeHtml(name)}</h4><p>Could not preview this sheet.</p></section>`;
      }
    })
    .filter(Boolean)
    .join("");

  if (!html) {
    throw new Error("Could not preview this spreadsheet.");
  }

  return `<div class="doc-preview-html">${html}</div>`;
}

/**
 * Renders office/text documents as HTML for in-app preview.
 * PDFs and images should use blob URL directly (iframe/img).
 */
export async function buildDocumentPreviewHtml(
  blobUrl,
  documentType,
  documentName,
) {
  const mode = getDocumentPreviewMode(documentType, documentName);
  const res = await fetch(blobUrl);
  const buffer = await res.arrayBuffer();

  if (mode === "spreadsheet") {
    const parsed = readSpreadsheetWorkbook(buffer);
    if (parsed.kind === "html") {
      return { mode: "html", html: parsed.html };
    }
    return { mode: "html", html: buildSpreadsheetPreviewHtml(parsed.wb) };
  }

  if (mode === "word") {
    const ext = String(documentName || "")
      .split(".")
      .pop()
      ?.toLowerCase();
    if (ext === "doc") {
      throw new Error(
        "Preview is not supported for legacy .doc files. Download the file to open it in Word.",
      );
    }
    const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
    return { mode: "html", html: result.value };
  }

  if (mode === "text") {
    const text = new TextDecoder().decode(buffer);
    return {
      mode: "html",
      html: `<pre class="doc-preview-text">${escapeHtml(text)}</pre>`,
    };
  }

  return { mode: "embed", html: null };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
